import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Filter as FilterIcon, X as XIcon } from "lucide-react";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { WatchCard } from "@/components/site/WatchCard";
import { ConciergeBand } from "@/pages/site/Home";

const KEYS = { brand: "marca", segment: "segmento", style: "estilo", availability: "disponibilidade", offer: "oportunidade", size: "tamanho", sort: "ordem" };

function FilterSelect({ label, value, onChange, options, testid }) {
  const { t } = useT();
  return (
    <label className="block w-full sm:min-w-[150px] sm:flex-1">
      <span className="text-[10px] uppercase tracking-[0.25em] text-[#A39E93]">{label}</span>
      <select className="field !py-2" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
        <option value="">{t.all}</option>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}

export default function Catalog() {
  const { t, r } = useT();
  const { settings } = useSite();
  const [sp, setSp] = useSearchParams();
  const [brands, setBrands] = useState([]);
  const [items, setItems] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const get = (k) => sp.get(KEYS[k]) || "";
  const setParam = (k) => (v) => { const n = new URLSearchParams(sp); v ? n.set(KEYS[k], v) : n.delete(KEYS[k]); setSp(n, { replace: true }); };

  useEffect(() => { api.get("/public/brands").then((x) => setBrands(x.data)); }, []);
  useEffect(() => {
    setItems(null);
    api.get("/public/watches", { params: { brand: get("brand") || undefined, segment: get("segment") || undefined, style: get("style") || undefined, availability: get("availability") || undefined, offer: get("offer") === "1" || undefined, size: get("size") || undefined, sort: get("sort") || "recent" } }).then((x) => setItems(x.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp]);

  const tx = settings?.taxonomies || {};
  const activeKeys = ["brand", "segment", "style", "availability", "offer", "size"];
  const activeCount = activeKeys.filter(get).length;
  const hasFilters = activeCount > 0;
  const pt = t.clear.toLowerCase() === "limpar";
  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-10 pt-10 sm:pt-16" data-testid="catalog-page">
      <p className="eyebrow">GoldenTime</p>
      <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl text-[#F3EFE6]">{t.catalog_title}</h1>
      <p className="mt-5 text-[#A39E93] max-w-lg">{t.catalog_sub}</p>

      {/* Mobile: compact filters bar with toggle + inline sort */}
      <div className="mt-10 sm:hidden flex items-center justify-between gap-3 border-b border-[#282520] pb-4">
        <button type="button" onClick={() => setFiltersOpen(!filtersOpen)} className="flex items-center gap-2 text-sm text-[#E6D5AC]" data-testid="catalog-filters-toggle"><FilterIcon className="w-4 h-4" /> {filtersOpen ? (pt ? "Esconder" : "Hide") : (pt ? "Filtros" : "Filters")}{activeCount > 0 && <span className="bg-[#C8AE6E] text-[#0C0B09] text-[10px] px-1.5 py-0.5 rounded-full tabular-nums">{activeCount}</span>}</button>
        <label className="flex-1 max-w-[180px]">
          <select className="field !py-1.5 text-sm" value={get("sort") || "recent"} onChange={(e) => setParam("sort")(e.target.value === "recent" ? "" : e.target.value)} data-testid="catalog-sort-mobile">
            <option value="recent">{t.sort_recent}</option><option value="price_asc">{t.sort_price_asc}</option><option value="price_desc">{t.sort_price_desc}</option>
          </select>
        </label>
      </div>

      <div className={`mt-6 sm:mt-14 ${filtersOpen ? "grid" : "hidden"} sm:flex flex-wrap gap-5 sm:gap-6 items-end border-b border-[#282520] pb-6 sm:pb-8`} data-testid="catalog-filters">
        <FilterSelect label={t.brand} value={get("brand")} onChange={setParam("brand")} options={brands.map((b) => [b.slug, `${b.name} (${b.count})`])} testid="filter-brand" />
        <FilterSelect label={t.segment} value={get("segment")} onChange={setParam("segment")} options={(tx.segments || []).map((s) => [s, s])} testid="filter-segment" />
        <FilterSelect label={t.style} value={get("style")} onChange={setParam("style")} options={(tx.styles || []).map((s) => [s, s])} testid="filter-style" />
        <FilterSelect label={t.availability} value={get("availability")} onChange={setParam("availability")} options={Object.entries(t.avail)} testid="filter-availability" />
        <FilterSelect label={t.case_size} value={get("size")} onChange={setParam("size")} options={Object.entries(t.sizes)} testid="filter-size" />
        <label className="flex items-center gap-2 text-sm text-[#F3EFE6]/85 cursor-pointer self-end pb-2 w-full sm:w-auto"><input type="checkbox" className="accent-[#C8AE6E] w-4 h-4" checked={get("offer") === "1"} onChange={(e) => setParam("offer")(e.target.checked ? "1" : "")} data-testid="filter-offer" />{t.offer}</label>
        <label className="hidden sm:block min-w-[170px] flex-1">
          <span className="text-[10px] uppercase tracking-[0.25em] text-[#A39E93]">{t.sort}</span>
          <select className="field !py-2" value={get("sort") || "recent"} onChange={(e) => setParam("sort")(e.target.value === "recent" ? "" : e.target.value)} data-testid="catalog-sort">
            <option value="recent">{t.sort_recent}</option><option value="price_asc">{t.sort_price_asc}</option><option value="price_desc">{t.sort_price_desc}</option>
          </select>
        </label>
        {hasFilters && <button onClick={() => setSp(new URLSearchParams(get("sort") ? { [KEYS.sort]: get("sort") } : {}))} className="text-xs uppercase tracking-[0.2em] text-[#C8AE6E] pb-1 sm:pb-3 flex items-center gap-1" data-testid="filters-clear"><XIcon className="w-3 h-3" /> {t.clear}</button>}
      </div>

      <p className="mt-6 text-xs tracking-[0.2em] uppercase text-[#A39E93]" data-testid="catalog-count">{items ? t.results(items.length) : "…"}</p>

      {items && items.length === 0 ? (
        <div className="py-24 text-center" data-testid="catalog-empty">
          <h2 className="text-3xl text-[#F3EFE6]">{t.empty_title}</h2>
          <p className="mt-4 text-[#A39E93]">{t.empty_sub}</p>
          <Link to={`${r.orders}?tipo=especifico`} className="btn-gold mt-10" data-testid="catalog-empty-specific-cta">{t.concierge_cta} <ArrowRight className="w-4 h-4" /></Link>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-8 gap-y-16" data-testid="catalog-grid">
          {(items || []).map((w, i) => <WatchCard key={w.id} w={w} delay={Math.min(i, 8) * 70} />)}
        </div>
      )}
      {items && items.length > 0 && <ConciergeBand compact />}
    </div>
  );
}
